import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { format, isValid, parseISO } from 'date-fns';
import { GrantRowModel, GrantStatus, NodeRef, UserRef } from '../models/access.models';
import { rightDef } from '../util/access-catalog';
import { phoneOf, pluralForm, titleIn } from '../util/access-format';

export type ChipClass = 'ok' | 'off' | 'dup' | 'grp' | 'own' | 'end' | 'bad' | 'soon' | 'auto';

/** What the lists of the grants show of one grant: all the texts are made here, so the templates only print them. */
export interface GrantView {
  row: GrantRowModel;
  group: boolean;
  /** The title of the row: the person, the group, or the store / organization the grant was wrongly given to. */
  name: string;
  sub: string;
  statusText: string;
  statusCls: ChipClass;
  /** «2 права» / «повтор». */
  dupText: string;
  srcText: string;
  srcCls: ChipClass;
  meta: string;
  who: string;
  /** The reason a grant does not act. */
  reason: string;
  working: boolean;
  /** The one-click button of the row: `extend` for the expired, `enable` for the off, `removeDup` for the covered one. */
  action: 'extend' | 'enable' | 'removeDup' | null;
  active: boolean;
}

const SHOWN_RESOURCES = 5;
const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * `yyyy-MM-dd[Thh:mm…]` → `dd.MM.yyyy`. The calendar date is taken as the server wrote it (a grant "until 31.12" ends at 23:59:59 of the server's day), so the page shows
 * the same date in any time zone of the browser; a string that is not a date is shown as it is.
 */
export function fmtDate(iso: string | undefined | null): string {
  if (!iso) {
    return '';
  }
  const m = CALENDAR_DATE.exec(iso);
  if (m) {
    return `${m[3]}.${m[2]}.${m[1]}`;
  }
  const d = parseISO(iso);
  return isValid(d) ? format(d, 'dd.MM.yyyy') : iso;
}

export function fmtDay(iso: string | undefined | null): string {
  if (!iso) {
    return '';
  }
  const m = CALENDAR_DATE.exec(iso);
  if (m) {
    return `${m[3]}.${m[2]}`;
  }
  const d = parseISO(iso);
  return isValid(d) ? format(d, 'dd.MM') : iso;
}

export function fmtTime(iso: string): string {
  const d = parseISO(iso);
  return isValid(d) ? format(d, 'HH:mm') : '';
}

@Injectable({ providedIn: 'root' })
export class AccessGrantViewService {
  private readonly tr = inject(TranslocoService);

  viewOf(row: GrantRowModel, options: { withNode?: boolean; offDate?: boolean } = {}): GrantView {
    const lang = this.tr.getActiveLang();
    const t = (key: string, params?: Record<string, unknown>): string => this.tr.translate(key, params);
    const recipient = row.recipient;
    const group = recipient.kind === 'group';
    let name = '';
    let sub = '';
    let working = true;
    if (recipient.kind === 'user') {
      name = recipient.user.name;
      working = recipient.working;
      sub = [t(`access.roles.${recipient.roleKey}`), phoneOf(recipient.user), recipient.place ? titleIn(recipient.place.title, lang) : ''].filter(p => p.length > 0).join(' · ');
    } else if (recipient.kind === 'group') {
      name = titleIn(recipient.node.title, lang);
      sub = [t('access.grant.typeGroup'), ...recipient.path.slice(-2).reverse().map(p => titleIn(p.title, lang))].join(' · ');
    } else {
      name = recipient.title;
      sub = t(recipient.kind === 'store' ? 'access.grant.typeStore' : 'access.grant.typeOrganization');
    }

    const status = this.statusOf(row.status, row, options.offDate === true);
    const source = this.sourceOf(row);
    const dupText = row.repeatOf !== null ? t('access.grant.repeatOf') : row.repeats > 0 ? `${row.repeats + 1} ${pluralForm(t('access.plural.grantsShort'), row.repeats + 1, lang)}` : '';
    const who = this.whoOf(row.created.by, row.created.at, 'access.grant.issuedBy');
    const metaParts = [
      options.withNode !== false && row.node ? `${t('access.grant.where')}: ${titleIn(row.node.title, lang)}` : '',
      this.resourcesText(row),
      this.termOf(row, t),
      source.meta,
      row.status.kind === 'off' ? this.whoOf(row.modified.by, row.modified.at, 'access.grant.offBy') : who
    ];
    return {
      row, group, name, sub, statusText: status.text, statusCls: status.cls, dupText, srcText: source.text, srcCls: source.cls,
      meta: metaParts.filter(p => p.length > 0).join(' · '), who, reason: status.reason, working,
      action: row.repeatOf !== null ? 'removeDup' : row.status.kind === 'expired' ? 'extend' : row.status.kind === 'off' ? 'enable' : null,
      active: row.active
    };
  }

  /**
   * What the grant is limited to: «52 организации: A, B, C, D, E и ещё 47». The noun follows the right (the auditor has organizations, prices and money flow have stores),
   * not only the kind the answer carries; at most five names are listed.
   */
  resourcesText(row: GrantRowModel): string {
    const resources = row.resources;
    if (!resources || resources.items.length === 0) {
      return '';
    }
    const lang = this.tr.getActiveLang();
    const kind = rightDef(row.key).resources ?? resources.kind;
    const n = resources.items.length;
    const noun = pluralForm(this.tr.translate(kind === 'organization' ? 'access.plural.organizations' : 'access.plural.stores'), n, lang);
    const shown = resources.items.slice(0, SHOWN_RESOURCES).map(i => i.title).join(', ');
    const more = n > SHOWN_RESOURCES ? ` ${this.tr.translate('access.grant.andMore', { n: n - SHOWN_RESOURCES })}` : '';
    return `${n} ${noun}: ${shown}${more}`;
  }

  /** The line of a group grant: «3 из 4 сотрудников — кроме Барри Аллена · выдал …». */
  groupLine(row: GrantRowModel): string {
    const lang = this.tr.getActiveLang();
    if (row.recipient.kind !== 'group') {
      return '';
    }
    const { members, except } = row.recipient;
    const taken = members - except.length;
    const names = except.map(u => u.name).join(', ');
    const head = except.length > 0
      ? `${taken} ${this.tr.translate('access.grant.outOf')} ${members} ${pluralForm(this.tr.translate('access.plural.employeesGen'), members, lang)} — ${this.tr.translate('access.grant.except')} ${names}`
      : `${members} ${pluralForm(this.tr.translate('access.plural.employees'), members, lang)}`;
    return [head, this.whoOf(row.created.by, row.created.at, 'access.grant.issuedBy')].filter(p => p.length > 0).join(' · ');
  }

  private statusOf(status: GrantStatus, row: GrantRowModel, offDate: boolean): { text: string; cls: ChipClass; reason: string } {
    const t = (key: string): string => this.tr.translate(key);
    switch (status.kind) {
      case 'acting':
        return { text: t('access.status.acting'), cls: 'ok', reason: '' };
      case 'notStarted':
        return { text: `${t('access.status.notStarted')} ${fmtDay(status.from ?? row.startDate)}`, cls: 'soon', reason: '' };
      case 'expired':
        return { text: `${t('access.status.expired')} ${fmtDay(status.to ?? row.endDate)}`, cls: 'end', reason: '' };
      case 'off':
        return { text: offDate ? `${t('access.status.off')} ${fmtDate(row.modified.at)}` : t('access.status.off'), cls: 'off', reason: '' };
      default:
        return { text: t('access.status.notActing'), cls: 'bad', reason: this.reasonText(status.reason) };
    }
  }

  /** The reason in words; a code that the page does not know yet is not shown as a key. */
  private reasonText(code: string | undefined): string {
    if (!code) {
      return '';
    }
    const key = `access.reason.${code}`;
    const text = this.tr.translate(key);
    return text === key ? this.tr.translate('access.reason.unknown') : text;
  }

  private sourceOf(row: GrantRowModel): { text: string; cls: ChipClass; meta: string } {
    const t = (key: string): string => this.tr.translate(key);
    switch (row.source) {
      case 'auto':
        return { text: t('access.source.auto'), cls: 'auto', meta: '' };
      case 'manager':
        return { text: t('access.source.own'), cls: 'own', meta: t('access.source.fromManager') };
      case 'adm-old':
        return { text: t('access.source.own'), cls: 'own', meta: t('access.source.fromOld') };
      case 'migration':
        return { text: t('access.source.migration'), cls: 'auto', meta: '' };
      default:
        return { text: t('access.source.own'), cls: 'own', meta: '' };
    }
  }

  private termOf(row: GrantRowModel, t: (key: string) => string): string {
    const status = row.status.kind;
    if (status === 'expired') {
      return `${t('access.term.was')} ${fmtDate(row.startDate ?? row.created.at)} — ${fmtDate(row.endDate)}`;
    }
    if (status === 'notStarted') {
      return `${t('access.term.starts')} ${fmtDate(row.startDate)}`;
    }
    if (row.startDate && row.endDate) {
      return `${t('access.term.acts')} ${fmtDate(row.startDate)} — ${fmtDate(row.endDate)}`;
    }
    if (row.endDate) {
      return `${t('access.term.until')} ${fmtDate(row.endDate)}`;
    }
    return status === 'off' || status === 'notActing' ? '' : t('access.term.forever');
  }

  private whoOf(by: UserRef | null, at: string, key: string): string {
    const name = by?.name ?? '';
    const date = fmtDate(at);
    if (!name) {
      return '';
    }
    return [this.tr.translate(key), name, date].filter(p => p.length > 0).join(' ');
  }

  nodeTitle(ref: NodeRef | null | undefined): string {
    return ref ? titleIn(ref.title, this.tr.getActiveLang()) : '';
  }
}
